import React, { useState, useEffect } from 'react';

import { Link } from 'react-router-dom';

import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Skeleton, Stack, styled, Typography } from '@mui/material';

import Axios from '../../../Assets/Axios/Axios';

import CustomNavigation from './CustomNavigation';

import { toCapitalize } from '../../../Helpers';
import getThumbnailImage from '../../../Helpers/getThumbnailImage';
import DefaultThumbnail from '../../../Assets/Thumbnails/Decoration.webp';

import { useIsMobile } from '../../../Hooks/useIsMobile';

import { PopularServicesQuery } from '../../../Server/Query';

const Slider1 = () => {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const isMobile = useIsMobile();

  useEffect(() => {
    Axios(PopularServicesQuery).then(({ data, errors }) => {
      if (errors) return undefined;
      const fetched = (data?.Services ?? []).map(service => ({
        ...service,
        banner_image:
          service.banner_image || getThumbnailImage(service.name) || DefaultThumbnail,
      }));
      setServices(fetched);
    });
  }, []);

  if (isLoading)
    return (
      <Stack direction='row' gap='2em' px={{ md: '31px' }} overflow='hidden'>
        <SkeletonCard flex={{ xs: '0 0 100%', md: '1 1 25%' }} />
        <SkeletonCard flex={{ xs: '0 0 100%', md: '1 1 25%' }} />
        <SkeletonCard flex={{ xs: '0 0 100%', md: '1 1 25%' }} />
        <SkeletonCard flex={{ xs: '0 0 100%', md: '1 1 25%' }} />
      </Stack>
    );

  return (
    <SwiperWithCss
      slidesPerView={1}
      spaceBetween={32}
      breakpoints={{ 414: { slidesPerView: 2.3 }, 900: { slidesPerView: 4 } }}
      style={{ paddingInline: isMobile ? undefined : '31px' }}
    >
      {services.map(service => (
        <SwiperSlide key={service.id}>
          <Link to={`/professional/${service.service_id}`}>
            <SlideWrapper>
              <Img
                src={service.banner_image}
                alt={toCapitalize(service.name)}
                onError={event => {
                  if (event.currentTarget.dataset.fallback) return;
                  event.currentTarget.dataset.fallback = 'true';
                  event.currentTarget.src = DefaultThumbnail;
                }}
              />
              <Scrim />
              <SlideContent>
                <SlideTextJob
                  variant={isMobile ? 'h6' : 'h5'}
                  component='h3'
                >
                  {toCapitalize(service.name)}
                </SlideTextJob>
                <SlideTextDesc>{service.description}</SlideTextDesc>
              </SlideContent>
            </SlideWrapper>
          </Link>
        </SwiperSlide>
      ))}
      {!isMobile && <CustomNavigation slidesCount={services.length} />}
    </SwiperWithCss>
  );
};

function SkeletonCard(props) {
  return (
    <Box
      {...props}
      variant='rectangular'
      borderRadius={4}
      sx={{ aspectRatio: '3/4' }}
      bgcolor='grey.200'
      boxSizing='border-box'
      p={2}
    >
      <Typography>
        <Skeleton sx={{ width: 'min(100%,250px)' }} />
      </Typography>
      <Typography variant='h5'>
        <Skeleton />
      </Typography>
      <Typography variant='h5'>
        <Skeleton width='60%' />
      </Typography>
    </Box>
  );
}

const Img = styled('img')({
  pointerEvents: 'none',
  minWidth: '0',
  aspectRatio: '3/4',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform .5s cubic-bezier(0.22,1,0.36,1)',
});

const SlideWrapper = styled(Box)({
  overflow: 'hidden',
  position: 'relative',
  borderRadius: '18px',
  cursor: 'pointer',
  '&:hover img': { transform: 'scale(1.07)' },
});

const Scrim = styled(Box)({
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(to top, rgba(1,42,52,0.9) 0%, rgba(1,42,52,0.35) 45%, rgba(1,42,52,0) 68%)',
  pointerEvents: 'none',
});

const SlideContent = styled(Box)({
  position: 'absolute',
  bottom: '18px',
  left: '18px',
  right: '18px',
});

const SlideTextJob = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontWeight: 600,
  color: 'white',
  lineHeight: 1.2,
});

const SlideTextDesc = styled(Typography)({
  fontWeight: 400,
  fontSize: '0.85rem',
  color: 'rgba(255,255,255,0.88)',
  marginTop: '4px',
  WebkitLineClamp: 2,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

const SwiperWithCss = styled(Swiper)({
  maxWidth: '100%',
  boxSizing: 'border-box',
});

export default Slider1;
